// @flow
import PrismType from 'prismjs';

// This function adds additional language configuration to the specified Prism instance.
// This function is called during startup (see src/apps/docs/client.js).
export const configurePrismLanguages = (Prism: PrismType): void => {
  // Bash extras
  Prism.languages.bash.function = {
    pattern: new RegExp(
      `${
        /(^|\s|;|\||&)(?:pip|easy_install|gem|npm|go|get)(?=$|\s|;|\||&)/.source
      }|${Prism.languages.bash.function.pattern.source}`,
    ),
  };

  Prism.languages.insertBefore('bash', 'function', {
    stripe: /(^|\s|;|\||&)(?:stripe)(?=$|\s|;|\||&)/,
    url: /(https?:\/\/)?\w*\.\w{2,3}[a-zA-Z0-9:.?=\/\-_]*/,
    option: / -{1,2}[a-zA-Z-]*/,
  });

  // Urls
  Prism.languages.url = {
    parameter: /[A-Za-z0-9\[\]-_]+(?=[=])/,
    value: {
      pattern: /([=])([^=&]*)/,
      lookbehind: true,
    },
  };

  // Iris
  Prism.languages.iris = {
    string: {
      pattern: /(["'])(\\(?:\r\n|[\s\S])|(?!\1)[^\\\r\n])*\1/,
      greedy: true,
    },
    keyword: /if|and|or/,
    function: /Block|Allow|Review/,
    operator: /--?|\+\+?|!=?=?|<=?|>=?|==?=?|&&?|\|\|?|\?|\*|\/|~|\^|%/,
    symbol: /:[a-zA-Z_][a-zA-Z_0-9]*:/,
    number: /\b-?(?:0x[\da-f]+|\d*\.?\d+(?:e[+-]?\d+)?)\b/i,
  };

  // Erb
  const erbOptions = {
    pattern: /(<%(.*)%>)/i,
    inside: Prism.languages.ruby,
    alias: 'language-ruby',
  };
  Prism.languages.erb = Prism.languages.html;
  Prism.languages.insertBefore('erb', 'tag', {
    erb: erbOptions,
  });

  // Objective-C extras + alias
  Prism.languages.objc = Prism.languages.objectivec;
  Prism.languages.insertBefore('objc', 'keyword', {
    builtin: Prism.languages.swift.builtin,
  });

  // Prism ruby extras
  Prism.languages.ruby.keyword = new RegExp(
    Prism.languages.ruby.keyword.source + /|private/.source,
  );
  Prism.languages.ruby.function = {
    pattern: /(\.)[a-z_][a-z0-9_]*(?=[\(\n \[,])/i,
    lookbehind: true,
  };

  // Prism curl
  Prism.languages.curl = {
    curl: /\bcurl\b/,
    url: /https?:[a-zA-Z0-9:.?=\/\-_{}]*/,
    parameter: {
      pattern: /(["'])?[A-Za-z0-9\[\]-_]+(["'])? *(?=[=])/,
    },
    value: [
      {
        pattern: /([=])([A-Za-z0-9-_.]*)/,
        lookbehind: true,
      },
      {
        pattern: /(["'])(\\(?:\r\n|[\s\S])|(?!\1)[^\\\r\n])*\1/,
      },
      {
        pattern: /(\-u )([A-Za-z0-9-_.{}]*)/,
        lookbehind: true,
      },
    ],
    option: / *-[a-zA-Z]*\b/,
  };

  // Prism Javascript extras
  Prism.languages.javascript.string.inside = {
    erb: erbOptions,
  };
  Prism.languages.insertBefore('javascript', 'punctuation', {
    erb: erbOptions,
  });
  Prism.languages.insertBefore('javascript', 'punctuation', {
    symbol: {
      pattern: /([a-zA-Z][a-zA-Z0-9-_]*)(?: *[:])/,
      inside: {
        punctuation: /:/,
      },
    },
    property: {
      pattern: /([.])[a-zA-Z-_]*/,
      lookbehind: true,
    },
  });

  // Prism PHP namespaces
  Prism.languages.insertBefore('php', 'package', {
    namespace: {
      pattern: /\\[A-Z][A-Za-z]+/,
    },
  });

  // Prism Python extras
  Prism.languages.insertBefore('python', 'comment', {
    parameter: {
      pattern: /([\(,] *(#.*)?[.\n]* *)[A-Za-z_]+ *(?=[=])/,
      lookbehind: true,
      inside: {
        operator: /=/,
      },
    },
  });
  Prism.languages.insertBefore('python', 'punctuation', {
    function: {
      pattern: /[A-Za-z]+(?=\()/,
      inside: {
        punctuation: /[.]/,
      },
    },
    constant: {
      pattern: /stripe[A-Za-z-_.]*/,
    },
    variable: {
      pattern: /([.])[A-Za-z-_]+/,
      lookbehind: true,
    },
  });

  // Prism Java extras
  Prism.languages.insertBefore('java', 'class-name', {
    constant: {
      pattern: /\b[A-Z][a-zA-Z-_]*(<[A-Z][a-zA-Z, ]*>)?/,
    },
  });

  // Prism Go extras
  Prism.languages.insertBefore('go', 'comment', {
    symbol: {
      pattern: /([\{,] *(\/\/.*)?[.\n]* *)[A-Za-z]+:/,
      lookbehind: true,
      inside: {
        operator: /=/,
      },
    },
    constant: {
      pattern: /&[A-Za-z. ]*(?={)/,
    },
  });

  // Prism C#
  Prism.languages.clike = {
    comment: [
      {
        pattern: /(^|[^\\])\/\*[\w\W]*?\*\//,
        lookbehind: true,
      },
      {
        pattern: /(^|[^\\:])\/\/.*/,
        lookbehind: true,
      },
    ],
    string: {
      pattern: /(["'])(\\(?:\r\n|[\s\S])|(?!\1)[^\\\r\n])*\1/,
      greedy: true,
    },
    'class-name': {
      pattern: /((?:\b(?:class|interface|extends|implements|trait|instanceof|new)\s+)|(?:catch\s+\())[a-z0-9_\.\\]+/i,
      lookbehind: true,
      inside: {
        punctuation: /(\.|\\)/,
      },
    },
    keyword: /\b(if|else|while|do|for|return|in|instanceof|function|new|try|throw|catch|finally|null|break|continue)\b/,
    boolean: /\b(true|false)\b/,
    function: /[a-z0-9_]+(?=\()/i,
    number: /\b-?(?:0x[\da-f]+|\d*\.?\d+(?:e[+-]?\d+)?)\b/i,
    operator: /--?|\+\+?|!=?=?|<=?|>=?|==?=?|&&?|\|\|?|\?|\*|\/|~|\^|%/,
    punctuation: /[{}[\];(),.:]/,
  };

  Prism.languages.csharp = Prism.languages.extend('clike', {
    keyword: /\b(abstract|as|async|await|base|bool|break|byte|case|catch|char|checked|class|const|continue|decimal|default|delegate|do|double|else|enum|event|explicit|extern|false|finally|fixed|float|for|foreach|goto|if|implicit|in|int|interface|internal|is|lock|long|namespace|new|null|object|operator|out|override|params|private|protected|public|readonly|ref|return|sbyte|sealed|short|sizeof|stackalloc|static|string|struct|switch|this|throw|true|try|typeof|uint|ulong|unchecked|unsafe|ushort|using|virtual|void|volatile|while|add|alias|ascending|async|await|descending|dynamic|from|get|global|group|into|join|let|orderby|partial|remove|select|set|value|var|where|yield)\b/,
    string: [
      {
        pattern: /@("|')(\1\1|\\\1|\\?(?!\1)[\s\S])*\1/,
        greedy: true,
      },
      {
        pattern: /("|')(\\?.)*?\1/,
        greedy: true,
      },
    ],
    number: /\b-?(0x[\da-f]+|\d*\.?\d+f?)\b/i,
  });
  Prism.languages.dotnet = Prism.languages.csharp;

  // Prism C# extras
  Prism.languages.insertBefore('csharp', 'keyword', {
    'generic-method': {
      pattern: /[a-z0-9_]+\s*<[^>\r\n]+?>\s*(?=\()/i,
      alias: 'function',
      inside: {
        keyword: Prism.languages.csharp.keyword,
        punctuation: /[<>(),.:]/,
      },
    },
    preprocessor: {
      pattern: /(^\s*)#.*/m,
      lookbehind: true,
      alias: 'property',
      inside: {
        // highlight preprocessor directives as keywords
        directive: {
          pattern: /(\s*#)\b(define|elif|else|endif|endregion|error|if|line|pragma|region|undef|warning)\b/,
          lookbehind: true,
          alias: 'keyword',
        },
      },
    },
  });

  // start Kotlin definition - copied from https://github.com/PrismJS/prism/blob/master/components/prism-kotlin.js
  Prism.languages.kotlin = Prism.languages.extend('clike', {
    keyword: {
      // The lookbehind prevents wrong highlighting of e.g. kotlin.properties.get
      pattern: /(^|[^.])\b(?:abstract|actual|annotation|as|break|by|catch|class|companion|const|constructor|continue|crossinline|data|do|dynamic|else|enum|expect|external|final|finally|for|fun|get|if|import|in|infix|init|inline|inner|interface|internal|is|lateinit|noinline|null|object|open|operator|out|override|package|private|protected|public|reified|return|sealed|set|super|suspend|tailrec|this|throw|to|try|typealias|val|var|vararg|when|where|while)\b/,
      lookbehind: true,
    },
    function: [
      /\w+(?=\s*\()/,
      {
        pattern: /(\.)\w+(?=\s*\{)/,
        lookbehind: true,
      },
    ],
    number: /\b(?:0[xX][\da-fA-F]+(?:_[\da-fA-F]+)*|0[bB][01]+(?:_[01]+)*|\d+(?:_\d+)*(?:\.\d+(?:_\d+)*)?(?:[eE][+-]?\d+(?:_\d+)*)?[fFL]?)\b/,
    operator: /\+[+=]?|-[-=>]?|==?=?|!(?:!|==?)?|[\/*%<>]=?|[?:]:?|\.\.|&&|\|\||\b(?:and|inv|or|shl|shr|ushr|xor)\b/,
  });

  delete Prism.languages.kotlin['class-name'];

  Prism.languages.insertBefore('kotlin', 'string', {
    'raw-string': {
      pattern: /("""|''')[\s\S]*?\1/,
      alias: 'string',
      // See interpolation below
    },
  });
  Prism.languages.insertBefore('kotlin', 'keyword', {
    annotation: {
      pattern: /\B@(?:\w+:)?(?:[A-Z]\w*|\[[^\]]+\])/,
      alias: 'builtin',
    },
  });
  Prism.languages.insertBefore('kotlin', 'function', {
    label: {
      pattern: /\w+@|@\w+/,
      alias: 'symbol',
    },
  });

  Prism.languages.kotlin.string.inside = Prism.languages.kotlin[
    'raw-string'
  ].inside = {
    interpolation: [
      {
        pattern: /\$\{[^}]+\}/,
        inside: {
          delimiter: {
            pattern: /^\$\{|\}$/,
            alias: 'variable',
          },
          rest: Prism.languages.kotlin,
        },
      },
      {
        pattern: /\$\w+/,
        alias: 'variable',
      },
    ],
  };

  // end Kotlin definition

  // Prism SQL extras
  Prism.languages.sql = {
    comment: {
      pattern: /(^|[^\\])(?:\/\*[\s\S]*?\*\/|(?:--|\/\/|#).*)/,
      lookbehind: !0,
    },
    string: {
      pattern: /(^|[^@\\])("|')(?:\\?[\s\S])*?\2/,
      greedy: !0,
      lookbehind: !0,
    },
    variable: /@[\w.$]+|@("|'|`)(?:\\?[\s\S])+?\1/,
    function: /\b(?:COUNT|SUM|AVG|MIN|MAX|FIRST|LAST|UCASE|LCASE|MID|LEN|ROUND|NOW|FORMAT)(?=\s*\()/i,
    keyword: /\b(?:ACTION|ADD|AFTER|ALGORITHM|ALL|ALTER|ANALYZE|ANY|APPLY|AS|ASC|AUTHORIZATION|AUTO_INCREMENT|BACKUP|BDB|BEGIN|BERKELEYDB|BIGINT|BINARY|BIT|BLOB|BOOL|BOOLEAN|BREAK|BROWSE|BTREE|BULK|BY|CALL|CASCADED?|CASE|CHAIN|CHAR VARYING|CHARACTER (?:SET|VARYING)|CHARSET|CHECK|CHECKPOINT|CLOSE|CLUSTERED|COALESCE|COLLATE|COLUMN|COLUMNS|COMMENT|COMMIT|COMMITTED|COMPUTE|CONNECT|CONSISTENT|CONSTRAINT|CONTAINS|CONTAINSTABLE|CONTINUE|CONVERT|CREATE|CROSS|CURRENT(?:_DATE|_TIME|_TIMESTAMP|_USER)?|CURSOR|DATA(?:BASES?)?|DATE(?:TIME)?|DBCC|DEALLOCATE|DEC|DECIMAL|DECLARE|DEFAULT|DEFINER|DELAYED|DELETE|DELIMITER(?:S)?|DENY|DESC|DESCRIBE|DETERMINISTIC|DISABLE|DISCARD|DISK|DISTINCT|DISTINCTROW|DISTRIBUTED|DO|DOUBLE(?: PRECISION)?|DROP|DUMMY|DUMP(?:FILE)?|DUPLICATE KEY|ELSE|ENABLE|ENCLOSED BY|END|ENGINE|ENUM|ERRLVL|ERRORS|ESCAPE(?:D BY)?|EXCEPT|EXEC(?:UTE)?|EXISTS|EXIT|EXPLAIN|EXTENDED|FETCH|FIELDS|FILE|FILLFACTOR|FIRST|FIXED|FLOAT|FOLLOWING|FOR(?: EACH ROW)?|FORCE|FOREIGN|FREETEXT(?:TABLE)?|FROM|FULL|FUNCTION|GEOMETRY(?:COLLECTION)?|GLOBAL|GOTO|GRANT|GROUP|HANDLER|HASH|HAVING|HOLDLOCK|IDENTITY(?:_INSERT|COL)?|IF|IGNORE|IMPORT|INDEX|INFILE|INNER|INNODB|INOUT|INSERT|INT|INTEGER|INTERSECT|INTO|INVOKER|ISOLATION LEVEL|JOIN|KEYS?|KILL|LANGUAGE SQL|LAST|LEFT|LIMIT|LINENO|LINES|LINESTRING|LOAD|LOCAL|LOCK|LONG(?:BLOB|TEXT)|MATCH(?:ED)?|MEDIUM(?:BLOB|INT|TEXT)|MERGE|MIDDLEINT|MODIFIES SQL DATA|MODIFY|MULTI(?:LINESTRING|POINT|POLYGON)|NATIONAL(?: CHAR VARYING| CHARACTER(?: VARYING)?| VARCHAR)?|NATURAL|NCHAR(?: VARCHAR)?|NEXT|NO(?: SQL|CHECK|CYCLE)?|NONCLUSTERED|NULLIF|NUMERIC|OFF?|OFFSETS?|ON|OPEN(?:DATASOURCE|QUERY|ROWSET)?|OPTIMIZE|OPTION(?:ALLY)?|ORDER|OUT(?:ER|FILE)?|OVER|PARTIAL|PARTITION|PERCENT|PIVOT|PLAN|POINT|POLYGON|PRECEDING|PRECISION|PREV|PRIMARY|PRINT|PRIVILEGES|PROC(?:EDURE)?|PUBLIC|PURGE|QUICK|RAISERROR|READ(?:S SQL DATA|TEXT)?|REAL|RECONFIGURE|REFERENCES|RELEASE|RENAME|REPEATABLE|REPLICATION|REQUIRE|RESTORE|RESTRICT|RETURNS?|REVOKE|RIGHT|ROLLBACK|ROUTINE|ROW(?:COUNT|GUIDCOL|S)?|RTREE|RULE|SAVE(?:POINT)?|SCHEMA|SELECT|SERIAL(?:IZABLE)?|SESSION(?:_USER)?|SET(?:USER)?|SHARE MODE|SHOW|SHUTDOWN|SIMPLE|SMALLINT|SNAPSHOT|SOME|SONAME|START(?:ING BY)?|STATISTICS|STATUS|STRIPED|SYSTEM_USER|TABLES?|TABLESPACE|TEMP(?:ORARY|TABLE)?|TERMINATED BY|TEXT(?:SIZE)?|THEN|TIMESTAMP|TINY(?:BLOB|INT|TEXT)|TOP?|TRAN(?:SACTIONS?)?|TRIGGER|TRUNCATE|TSEQUAL|TYPES?|UNBOUNDED|UNCOMMITTED|UNDEFINED|UNION|UNIQUE|UNPIVOT|UPDATE(?:TEXT)?|USAGE|USE|USER|USING|VALUES?|VAR(?:BINARY|CHAR|CHARACTER|YING)|VIEW|WAITFOR|WARNINGS|WHEN|WHERE|WHILE|WITH(?: ROLLUP|IN)?|WORK|WRITE(?:TEXT)?)\b/i,
    boolean: /\b(?:TRUE|FALSE|NULL)\b/i,
    number: /\b-?(?:0x)?\d*\.?[\da-f]+\b/,
    operator: /[-+*\/=%^~]|&&?|\|?\||!=?|<(?:=>?|<|>)?|>[>=]?|\b(?:AND|BETWEEN|IN|LIKE|NOT|OR|IS|DIV|REGEXP|RLIKE|SOUNDS LIKE|XOR)\b/i,
    punctuation: /[;[\]()`,.]/,
  };

  Prism.languages.markdoc = {
    tag: {
      pattern: /{%(.|\n)*?%}/i,
      inside: {
        tagType: {
          pattern: /^({%\s*\/?)(\w*|-)* /i,
          lookbehind: true,
        },
        id: /#(\w|-)*\b/,
        string: /".*?"/,
        equals: /=/,
      },
    },
  };

  // Prism groovy extras
  (Prism.languages.groovy = Prism.languages.extend('clike', {
    keyword: /\b(as|def|in|abstract|assert|boolean|break|byte|case|catch|char|class|const|continue|default|do|double|else|enum|extends|final|finally|float|for|goto|if|implements|import|instanceof|int|interface|long|native|new|package|private|protected|public|return|short|static|strictfp|super|switch|synchronized|this|throw|throws|trait|transient|try|void|volatile|while)\b/,
    string: [
      {
        pattern: /("""|''')[\s\S]*?\1|(\$\/)(\$\/\$|[\s\S])*?\/\$/,
        greedy: !0,
      },
      {pattern: /("|'|\/)(?:\\?.)*?\1/, greedy: !0},
    ],
    number: /\b(?:0b[01_]+|0x[\da-f_]+(?:\.[\da-f_p\-]+)?|[\d_]+(?:\.[\d_]+)?(?:e[+-]?[\d]+)?)[glidf]?\b/i,
    operator: {
      pattern: /(^|[^.])(~|==?~?|\?[.:]?|\*(?:[.=]|\*=?)?|\.[@&]|\.\.<|\.{1,2}(?!\.)|-[-=>]?|\+[+=]?|!=?|<(?:<=?|=>?)?|>(?:>>?=?|=)?|&[&=]?|\|[|=]?|\/=?|\^=?|%=?)/,
      lookbehind: !0,
    },
    punctuation: /\.+|[{}[\];(),:$]/,
  })),
    Prism.languages.insertBefore('groovy', 'string', {
      shebang: {pattern: /#!.+/, alias: 'comment'},
    }),
    Prism.languages.insertBefore('groovy', 'punctuation', {
      'spock-block': /\b(setup|given|when|then|and|cleanup|expect|where):/,
    }),
    Prism.languages.insertBefore('groovy', 'function', {
      annotation: {
        alias: 'punctuation',
        pattern: /(^|[^.])@\w+/,
        lookbehind: !0,
      },
    });

  // Prism Markup extras
  Prism.languages.markup.tag.inside['attr-value'].inside = {
    punctuation: /[=>]/,
    string: /("|')(\\(?:\r\n|[\w\W])|(?!\1)[^\\\r\n])*\1/,
  };
};
